import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
  Button,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styled } from '@mui/styles';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useEffect, useReducer, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { createPortal } from 'react-dom';
import { useProductFamilyById } from '../hooks/useProductFamilyById';
import PdfFileIcon from '../../../_assets/svg/PdfFile.svg';
import useNotifications from '../../../_lib/notifications/useNotifications';
import TermsOfUseDialog from './TermsOfUseDialog';
import getRibbonVariables from '../utils/getRibbonVariables';
import ResilienceLayout from './ResilienceLayout';
import { ServiceStatus } from '../types';
import { useCheckAnyPolicyHasStatus } from '../../../components/hooks/queries/policies/useCheckAnyPolicyHasStatus';
import { shouldDisplayRibbon } from '../utils/shouldDisplayRibbon';
import {
  EXPIRED_STATUS,
  RENEWED_STATUS,
} from '../../../console/_statics/policy.statics';
import { ProductFamilyCode } from '../types';
import { useActor } from '../../../components/hooks/useActor';
import { useServiceStatusInfo } from '../hooks/useServiceStatusInfo';

const ServiceIntro = () => {
  const [open, setOpen] = useState(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const dynamicActionButtonContainerRef = useRef<Element | null>(null);
  const { productFamilyId } = useParams<{ productFamilyId: string }>();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const notification = useNotifications();
  const actor = useActor();

  // Detect if current user is in Pool tenant
  const isPoolTenant = actor.isPool || location.pathname.startsWith('/pool');

  // Determine the correct base path based on tenant
  const basePath = isPoolTenant
    ? '/pool/resilience/catalog'
    : '/customer/resilience/catalog';

  const hasExpiredPolicy = useCheckAnyPolicyHasStatus(EXPIRED_STATUS);
  const hasRenewedPolicy = useCheckAnyPolicyHasStatus(RENEWED_STATUS);

  const { data: productFamily } = useProductFamilyById(productFamilyId);

  // Call useServiceStatusInfo for all tenants - the hook will handle Pool tenant logic internally
  const { data: serviceStatusInfos = [] } = useServiceStatusInfo();

  const serviceStatusInfo = serviceStatusInfos.find(
    (_serviceStatusInfo) =>
      _serviceStatusInfo.productFamily.id === productFamilyId
  );
  const serviceStatus = serviceStatusInfo?.status ?? ServiceStatus.NotActivated;

  useEffect(() => {
    if (!dynamicActionButtonContainerRef.current && productFamily?.intro) {
      const dynamicActionButtonContainer = document.querySelector(
        '#service-external-intro #dynamic-action-button-container'
      );
      if (dynamicActionButtonContainer) {
        dynamicActionButtonContainerRef.current = dynamicActionButtonContainer;
        forceUpdate();
      }
    }
  }, [productFamily?.intro]);

  if (!productFamily) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const ribbonVariables = getRibbonVariables(theme, productFamily?.ribbon);

  const handleDownloadBrochure = () => {
    if (!productFamily?.brochureUrl) {
      console.error('No brochure URL found: ', productFamily);
      notification.show({
        type: 'error',
        body: 'Sorry, the brochure is currently unavailable for download.',
        title: 'Download Error',
        position: 'top-right',
      });
      return;
    }

    window.open(productFamily.brochureUrl, '_blank');
  };

  const handleOpen = () => {
    if (!productFamily.agreementUrl) {
      console.error('No agreement URL found: ', productFamily);
      notification.show({
        type: 'error',
        body: 'Sorry, the terms of use document is currently unavailable.',
        title: 'Terms of Use Error',
        position: 'top-right',
      });
      return;
    }

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const sanitizedExternalIntro = DOMPurify.sanitize(productFamily?.intro || '');

  const isRibbonVisible = shouldDisplayRibbon({
    uniqueCode: productFamily.code,
    hasExpiredPolicy,
    hasRenewedPolicy,
    ribbon: productFamily.ribbon,
    serviceStatus,
  });

  return (
    <ResilienceLayout>
      <Paper sx={{ position: 'relative', padding: '2rem' }}>
        {isRibbonVisible && (
          <StyledRibbon
            bgColor={ribbonVariables.bgColor}
            textColor={ribbonVariables.textColor}
          >
            {productFamily.ribbon}
          </StyledRibbon>
        )}
        <TermsOfUseDialog
          open={open}
          onClose={handleClose}
          pdfUrl={productFamily.agreementUrl}
        />
        <Box
          display="grid"
          gridTemplateColumns="1fr"
          gridTemplateRows="max-content"
          margin="-2rem"
          padding="2rem"
          sx={{
            background: `url(${productFamily.bannerUrl}) no-repeat center right / cover`,
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <div>
            <Button
              variant="text"
              startIcon={<ChevronLeftIcon />}
              onClick={history.goBack}
              sx={{ color: theme.palette.common.grey12, marginLeft: '-1rem' }}
              data-testid="back-button "
            >
              Back
            </Button>
          </div>
          <div style={{ margin: '1.5rem 1.5rem', width: '40rem' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1rem',
                lineHeight: '1.25rem',
                opacity: '0.7',
                color: theme.palette.common.white,
              }}
            >
              {productFamily.summary}
            </Typography>
            <StyledHeaderItem2>
              <SolidButton
                variant="outlined"
                color="primary"
                onClick={() =>
                  history.push(
                    `${basePath}/services/${productFamilyId}/pricing`
                  )
                }
                size="large"
              >
                View Plans
              </SolidButton>
              {[
                ServiceStatus.RequestReceived,
                ServiceStatus.Activated,
                ServiceStatus.ActivationInProgress,
                ServiceStatus.TestingInProgress,
              ].includes(serviceStatus) && (
                <SolidButton
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (
                      productFamily.code === ProductFamilyCode.CTS &&
                      serviceStatus === ServiceStatus.Activated
                    ) {
                      // For Pool tenants, we don't have training functionality
                      if (isPoolTenant) {
                        history.push(
                          `${basePath}/services/${productFamilyId}/pricing`
                        );
                        return;
                      }
                      history.push('/customer/training');
                      return;
                    }

                    // Navigate to appropriate services path based on tenant
                    const servicesPath = isPoolTenant
                      ? '/pool/resilience/catalog'
                      : '/customer/resilience/services';
                    history.push(servicesPath);
                  }}
                >
                  {productFamily.code === ProductFamilyCode.CTS &&
                  serviceStatus === ServiceStatus.Activated
                    ? 'Go to Training'
                    : 'Go to My Services'}
                </SolidButton>
              )}
            </StyledHeaderItem2>
          </div>
        </Box>
        <Box>
          <Typography variant="body1" gutterBottom>
            <div
              style={{ overflow: 'hidden' }}
              id="service-external-intro"
              data-testid="service-external-intro"
            >
              {parse(sanitizedExternalIntro)}
            </div>
          </Typography>
        </Box>
        {dynamicActionButtonContainerRef.current &&
          createPortal(
            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="flex-end"
              gap={2}
            >
              {productFamily.agreementUrl && (
                <OutlinedButton onClick={handleOpen}>
                  Terms of Use
                </OutlinedButton>
              )}
              <OutlinedButton
                variant="outlined"
                startIcon={<PdfFileIcon />}
                onClick={handleDownloadBrochure}
              >
                Download Brochure
              </OutlinedButton>
              {[
                ServiceStatus.RequestReceived,
                ServiceStatus.Activated,
                ServiceStatus.ActivationInProgress,
                ServiceStatus.TestingInProgress,
              ].includes(serviceStatus) ? (
                <SolidButton
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (
                      productFamily.code === ProductFamilyCode.CTS &&
                      serviceStatus === ServiceStatus.Activated
                    ) {
                      // For Pool tenants, we don't have training functionality
                      if (isPoolTenant) {
                        history.push(
                          `${basePath}/services/${productFamilyId}/pricing`
                        );
                        return;
                      }
                      history.push('/customer/training');
                      return;
                    }

                    // Navigate to appropriate services path based on tenant
                    const servicesPath = isPoolTenant
                      ? '/pool/resilience/catalog'
                      : '/customer/resilience/services';
                    history.push(servicesPath);
                  }}
                >
                  {productFamily.code === ProductFamilyCode.CTS &&
                  serviceStatus === ServiceStatus.Activated
                    ? 'Go to Training'
                    : 'Go to My Services'}
                </SolidButton>
              ) : (
                <SolidButton
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    history.push(
                      `${basePath}/services/${productFamilyId}/pricing`
                    )
                  }
                  size="large"
                >
                  View Plans
                </SolidButton>
              )}
            </Box>,
            dynamicActionButtonContainerRef.current
          )}
      </Paper>
    </ResilienceLayout>
  );
};

const OutlinedButton = styled(Button)(({ theme }) => ({
  borderColor: theme.palette.primary.cowbellBlue,
  color: theme.palette.primary.cowbellBlue,
  border: `1px solid ${theme.palette.primary.cowbellBlue}`,
  height: 'auto',

  '&:hover': {
    borderColor: theme.palette.primary.dark,
    color: theme.palette.primary.dark,
  },
}));

const SolidButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.cowbellBlue,
  color: theme.palette.common.white,
  fontWeight: 700,
  height: '100%',
  borderColor: theme.palette.primary.cowbellBlue,

  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.cowbellBlue,
  },

  '&:focus': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const StyledHeaderItem2 = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '1rem',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  height: '4rem',
  marginTop: '1.5rem',

  [theme.breakpoints.up('md')]: {
    height: '3.5rem',
  },

  [theme.breakpoints.up('lg')]: {
    height: '3rem',
  },
}));

const StyledRibbon = styled('div')(
  ({ bgColor, textColor }: { bgColor: string; textColor: string }) => ({
    '--f': '.5em' /* control the folded part*/,
    '--r': '.8em' /* control the ribbon shape */,

    position: 'absolute',
    top: '20px',
    right: 'calc(-1*var(--f))',
    'padding-inline': '1em',
    'line-height': 1.8,
    background: bgColor,
    'border-bottom': 'var(--f) solid #0005',
    'border-left': 'var(--r) solid #0000',
    'clip-path': `polygon(0 0,100% 0,100% calc(100% - var(--f)),calc(100% - var(--f)) 100%,
      calc(100% - var(--f)) calc(100% - var(--f)),0 calc(100% - var(--f)),
      var(--r) calc(50% - var(--f)/2))`,
    zIndex: 1,
    'font-size': '1.25rem',
    color: textColor,
    'font-weight': 'bold',
  })
);

export default ServiceIntro;
